import "@assets/css/bootstrap-grid.css";

import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { Checkbox } from "@ui/external/components/checkbox";
import { FormSkeleton } from "@ui/external/components/skeleton";
import { ErrorLabel, FormLabel } from "@ui/internal/components/label";
import { getInputField } from "@utils/function/index.function";
import {
  changeSymbolstoDash,
  isEditorContentEmpty,
  makeSlug,
  showToast,
  throwErrorAlert,
} from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  FormsFormFormData,
  FormsFormSchema,
} from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { FormFormsData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

const FormsForm: React.FC = () => {
  const queryClient = useQueryClient();
  const { searchParams, handleAction } = useHandleAction({});
  const pathname = usePathname();
  const pageItem = searchParams.get("page-item");
  const pageMode = searchParams.get("page");
  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormsFormFormData>({
    mode: "onChange",
    resolver: zodResolver(FormsFormSchema),
  });
  console.log("Error", errors);
  console.log("Watch", watch());
  /* set default value */
  const { setIsRetrive }: any = usePending();
  const { setIsFormPending } = useSelectionStore();

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<FormFormsData>
  >({
    endpoint: endpoint["forms"],
    id: pageItem || "",
    key: "forms" + pageItem,
    enabled: pageItem ? true : false,
  });

  useEffect(() => {
    setIsRetrive(pageItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;

      if (firstResult) {
        if (pageMode == "add") {
          return reset({});
        } else {
          reset(() => ({
            slug: firstResult?.slug,
            title: firstResult?.title || "",
            auto_expiration: firstResult?.auto_expiration || false,
            category: [
              {
                label: firstResult?.category.name,
                value: firstResult?.category.id,
              },
            ],
            description: firstResult?.description || "",
            embedded_code: firstResult?.embedded_code || "",
            excerpt: firstResult?.excerpt || "",
            header_code: firstResult?.header_code || "",
            media: firstResult?.media || "",
            auto_expiration_date: firstResult?.auto_expiration_date || "",
            meta_title: isEditorContentEmpty(firstResult?.meta_title)
              ? ""
              : firstResult?.meta_title,
            meta_description: isEditorContentEmpty(
              firstResult?.meta_description
            )
              ? ""
              : firstResult?.meta_description,
          }));
        }
      }
    }
  }, [fetchData?.data, fetchPending]);

  const { mutate } = usePostData();
  useEffect(() => {
    if (pageMode === "add") {
      setValue("slug", changeSymbolstoDash(makeSlug(watch("title"))));
    }
    console.log("Auto Exp", watch("auto_expiration"));
    if (!watch("auto_expiration")) setValue("auto_expiration_date", "");
  }, [watch("title"), watch("auto_expiration")]);

  const handleMutate = (data: any) => {
    if (!data["auto_expiration"]) delete data["auto_expiration_date"];
    if (pageItem) {
      data = {
        ...data,
        id: pageItem,
      };
      // if (!(data.media instanceof File || data.media instanceof Blob))
      //   delete data.media;
    }
    data = { ...data, category: data["category"]?.[0]?.value };

    return {
      data,
      endpoint: endpoint["forms"],
      method: pageItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({
    status,
  }: {
    data: FormsFormFormData;
    status: number;
  }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      queryClient.invalidateQueries({ queryKey: ["forms-section-listing"] });

      showToast({
        description: `Forms ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "page-close",
      });
      setIsFormPending(false);
    } else {
      return new Error("Unknown API Error");
    }
  };
  const handleError = async (error: any) => {
    console.log(error, "error00");
    setIsFormPending(false);

    throwErrorAlert(error);
  };
  const onSubmit = (data: FormsFormFormData) => {
    setIsFormPending(true);
    console.log("data", data);
    //Add Mutation Data
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = [
    {
      name: "title",
      label: "Form Title",
      colSpan: 12,
      type: "text",
      placeholder: "Enter Title",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "slug",
      label: "Slug",
      colSpan: 6,
      type: "text",
      placeholder: "Enter Slug",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "category",
      label: "Select Category",
      colSpan: 6,
      type: "async",
      endpoint: endpoint["forms-category"],
      format: { key: "name", value: "id", mutate: "name" },
      settings: {
        add: true,
        debounce: 300,

        remove: true,
        update: true,
      },
      placeholder: "Select Section",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
      visable: true,
    },
    {
      name: "description",
      label: "Description ",
      colSpan: 12,
      type: "editor",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "header_code",
      label: "Header Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "embedded_code",
      label: "Embedded Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
  ];

  const sideFormSchema: FormField[] = [
    {
      name: "media",
      label: "Image ",
      colSpan: 12,
      type: "cropper",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "excerpt",
      label: "Excerpt ",
      colSpan: 12,
      type: "text",
      showLength: true,
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "",
      type: "component",
      className: "mb-[20px]",
      component: (
        <div className="flex flex-col mb-[20px] justify-between">
          {<FormLabel label="Enable Auto Expiration" />}
          <div className="flex gap-2 my-3    items-center">
            <Controller
              control={control}
              name="auto_expiration"
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <Checkbox
                    label="Yes"
                    checked={field.value === true}
                    onCheckedChange={(value: boolean) => {
                      if (value) {
                        field.onChange(true);
                      } else {
                        field.onChange(false);
                      }
                    }}
                    error={errors.auto_expiration?.message}
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="auto_expiration"
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <Checkbox
                    label="No"
                    checked={field.value === false}
                    onCheckedChange={(value: boolean) => {
                      if (value) {
                        field.onChange(false);
                      } else {
                        field.onChange(true);
                      }
                    }}
                    error={errors.auto_expiration?.message}
                  />
                  <ErrorLabel error={errors.auto_expiration?.message} />
                </div>
              )}
            />
          </div>
          <ErrorLabel message={errors.auto_expiration?.message} />
          <div className="flex gap-2 items-center"></div>
        </div>
      ),
    },
    {
      name: "auto_expiration_date",
      visable: watch("auto_expiration") ? true : false,
      type: "date",
      label: "Pick a date",
      colSpan: 12,
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full w-full`,
    },
    {
      name: "meta_title",
      accordian_label: "Meta Title",
      label: "Meta Title",
      colSpan: 12,
      type: "textarea",
      placeholder: "Meta title",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  rounded-md w-full`,
      visable: true,
    },
    {
      name: "meta_description",
      accordian_label: "Meta Description",
      label: "Meta Description",
      colSpan: 12,
      type: "textarea",
      placeholder: "Meta Description",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  rounded-md w-full`,
      visable: true,
    },
  ];

  if (fetchPending && pageItem)
    return (
      <>
        <FormSkeleton />
      </>
    );
  console.log("Errors", errors);
  return (
    <>
      <form
        id={pathname}
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full"
      >
        <div className="col-md-8">
          {" "}
          <div className="row">
            {formSchema.map(
              (field, index) =>
                (field.visable || field.visable == undefined) && (
                  <div
                    key={index}
                    className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
                  >
                    {getInputField({ ...field })}
                  </div>
                )
            )}
          </div>
        </div>
        <div className="col-md-4 col-12">
          {sideFormSchema.map(
            (field, index) =>
              (field.visable || field.visable == undefined) && (
                <div
                  key={index}
                  className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
                >
                  {getInputField({ ...field })}
                </div>
              )
          )}
        </div>
      </form>
    </>
  );
};

export default FormsForm;
